// pdf.js's `legacy` build still relies, unpolyfilled, on features missing from
// older browsers (e.g. Samsung Internet 23 = Chromium 115), so no PDF loads
// there (Sentry PROD-USER-6Z). Import for side effects before pdf.js runs, in
// both the API and the worker — see `PDFViewer.tsx` and `pdf.worker.ts`.

type PromiseWithResolvers<T> = {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason?: unknown) => void;
};

const PromiseConstructor = Promise as PromiseConstructor & {
  withResolvers?: <T>() => PromiseWithResolvers<T>;
};

// Chromium 119+, Safari 17.4+.
if (typeof PromiseConstructor.withResolvers !== 'function') {
  PromiseConstructor.withResolvers = <T>() => {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

// `for await (… of readableStream)`: Chromium 124+, Firefox 110+.
if (
  typeof ReadableStream !== 'undefined' &&
  !(Symbol.asyncIterator in ReadableStream.prototype)
) {
  Object.defineProperty(ReadableStream.prototype, Symbol.asyncIterator, {
    configurable: true,
    writable: true,
    async *value(this: ReadableStream) {
      const reader = this.getReader();
      let isDone = false;
      try {
        while (true) {
          // eslint-disable-next-line no-await-in-loop -- stream reads are sequential
          const { done, value } = await reader.read();
          if (done) {
            isDone = true;
            return;
          }
          yield value;
        }
      } finally {
        // Early `break`/`throw` cancels the stream, as the native iterator does.
        // A failed `cancel()` (e.g. on an already errored stream) must neither
        // replace the original error nor skip releasing the lock.
        try {
          if (!isDone) await reader.cancel();
        } catch {
          // Ignored: see above.
        } finally {
          reader.releaseLock();
        }
      }
    },
  });
}

export {};
